import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CMSCareers = () => {
  const { getAuthHeader } = useAuth();
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ title: '', department: '', location: '', type: 'Full-time', experience: '', description: '', is_active: true });

  useEffect(() => { fetchCareers(); }, []);

  const fetchCareers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/careers`, { headers: getAuthHeader() });
      setCareers(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/cms/careers/${editing.id}`, formData, { headers: getAuthHeader() });
      } else {
        await axios.post(`${BACKEND_URL}/api/cms/careers`, formData, { headers: getAuthHeader() });
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData({ title: '', department: '', location: '', type: 'Full-time', experience: '', description: '', is_active: true });
      fetchCareers();
    } catch (error) {
      alert('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/cms/careers/${id}`, { headers: getAuthHeader() });
      fetchCareers();
    } catch (error) {
      alert('Failed to delete');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <h2 className="text-2xl font-bold">Careers Management</h2>
        <Button onClick={() => { setEditing(null); setFormData({ title: '', department: '', location: '', type: 'Full-time', experience: '', description: '', is_active: true }); setDialogOpen(true); }} className="bg-primary">
          <Plus size={20} className="mr-2" /> Add Job
        </Button>
      </div>

      <div className="space-y-4">
        {careers.map((job) => (
          <Card key={job.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {job.title}
                    {job.is_active ? <Badge className="bg-green-500">Active</Badge> : <Badge className="bg-gray-500">Inactive</Badge>}
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">{job.department} • {job.location} • {job.type} • {job.experience}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => { setEditing(job); setFormData(job); setDialogOpen(true); }} size="sm" variant="outline"><Edit size={16} /></Button>
                  <Button onClick={() => handleDelete(job.id)} size="sm" variant="outline" className="text-red-600"><Trash2 size={16} /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700">{job.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Job</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-sm font-medium mb-2">Job Title *</label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required /></div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-2">Department *</label><Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} required /></div>
              <div><label className="block text-sm font-medium mb-2">Location *</label><Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-2">Type *</label><select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2"><option>Full-time</option><option>Part-time</option><option>Contract</option></select></div>
              <div><label className="block text-sm font-medium mb-2">Experience *</label><Input value={formData.experience} onChange={(e) => setFormData({ ...formData, experience: e.target.value })} placeholder="e.g., 3-5 years" required /></div>
            </div>
            <div><label className="block text-sm font-medium mb-2">Description *</label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={4} /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
              <label className="text-sm">Active (visible on website)</label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSCareers;
